
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import dayjs from "dayjs"
import { ApiStatus } from "@/store-services/api.types";

// Map
import { Feature, Graticule, Map, Overlay, View } from "ol";
import { fromLonLat } from "ol/proj";
import { Polygon } from "ol/geom";
import Layer from "ol/layer/Layer";
import TileLayer from "ol/layer/Tile";
import DayNight from "ol-ext/source/DayNight";
import XYZ from "ol/source/XYZ";
import Crop from "ol-ext/filter/Crop";
import { getWidth } from "ol/extent";
import { SpacecraftMapConfigInterface } from "./spacecraftmap.types";
import { api } from "../../services/api";
import { IDataTLEs } from "../../services/api.types";
import { Subscription } from "rxjs";
import { MapSpacecraft } from "./maps/spacecraft";
import { Fill, Stroke, Text } from "ol/style";

const TRACK_COLORS = ["#ff0000", "#ffff00", "#ff7000", "#00aa00", "#0055ff"];

@Component
export default class SpacecraftMapComponent extends Vue {
  @Prop() config!: SpacecraftMapConfigInterface;
  map!: Map;
  ZOOM: number = 1.25;
  CENTER = [0, 0];
  dayNightSource = new DayNight({});
  basemapNight = new TileLayer({
    source: new XYZ({
      url: "https://map1.vis.earthdata.nasa.gov/wmts-webmerc/VIIRS_CityLights_2012/default//GoogleMapsCompatible_Level8/{z}/{y}/{x}.jpg",
    }),
    zIndex: 1,
  });
  id: string = (Math.random() * 10000).toFixed(0);

  spacecraftLayers: Array<Layer> = []
  popups: Array<Overlay> = []
  showPopup: {all?: boolean, single?: {name: string, offset?: {x?: number, y?: number}}} = {}
  intervals = new Array<number>()

  TLES: IDataTLEs = {
    tles: [],
    timestamp: 0
  }
  TLEsub!: Subscription

  get statusStyle() {
    let style: { [key: string]: string } = {
      height: "0.7em",
      color: "#009abd", // blue
    };

    switch(this.status) {
      case "connected":
        style.color = "#00a400"; // green
        break;
      case "disconnected":
      case "error":
        style.color = "#e60000"; // red
        break;
    }

    return style;
  }

  get status(): ApiStatus {
    return this.$store.getters["api/getStatus"]();
  }

  get tleTimestamp(): string {
    return `${dayjs.utc(this.TLES.timestamp).format("DD.MM.YYYY, HH:mm:ss")} GMT`
  }

  get enabledSpacecraft(): Array<MapSpacecraft> {
    return this.config.spacecraft
      .filter(name => this.TLES.tles.find(tle => tle[0].includes(name)))
      .map((name, i) => {
        return new MapSpacecraft(this.TLES.tles.find(tle => tle[0].includes(name)) as [string, string, string], TRACK_COLORS[i])
      })
  }

  get followSpacecraft() {
    return this.config.followSpacecraft
      && this.config.followSpacecraft.enabled ? this.config.followSpacecraft.name : ""
  }
  set followSpacecraft(value: string) {
    this.config.followSpacecraft.enabled = value !== ""
  }

  updateNightLayer() {
    this.map.removeLayer(this.basemapNight);
    let coordinates = (
      this.dayNightSource.getCoordinates() as unknown as Array<Array<number>>
    ).map((p: Array<number>) => fromLonLat(p, "EPSG:3857"));
    let filter = new Crop({
      feature: new Feature(new Polygon([coordinates])),
      inner: false,
      wrapX: true
    });
    this.basemapNight.getFilters().forEach((f) => {
      this.basemapNight.removeFilter(f);
    });
    this.basemapNight.addFilter(filter);
    this.map.addLayer(this.basemapNight);
    filter.setActive(true);
    this.$forceUpdate();
  }

  mounted() {
    this.map = new Map({
      target: this.$refs["map-root"] as HTMLElement,
      layers: [
        // Basemap
        new TileLayer({
          source: new XYZ({
            url: "https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}",
          }),
          zIndex: 0,
        }),
        // Coordinate grid
        new Graticule({
          strokeStyle: new Stroke({
            color: 'grey',
            width: 0.5
          }),
          showLabels: true,
          latLabelStyle: new Text({
            textAlign: 'end',
            fill: new Fill({
              color: 'white'
            }),
            stroke: new Stroke({})
          }),
          lonLabelStyle: new Text({
            textBaseline: 'bottom',
            fill: new Fill({
              color: 'grey'
            }),
            stroke: new Stroke({})
          }),
          zIndex: 2
        }),
        // Continent, country, city overlay
        new TileLayer({
          source: new XYZ({
            url: "https://tiles.stadiamaps.com/tiles/stamen_toner_labels/{z}/{x}/{y}.png",
          }),
          zIndex: 10,
        }),
      ],
      view: new View({
        zoom: this.ZOOM,
        minZoom: 0.75,
        maxZoom: 6,
        center: this.CENTER,
        constrainRotation: true,
        multiWorld: true
      }),
      controls: [],
    });

    this.map.on("pointermove", (evt) => {
      if (evt.dragging) return;
      let feature = this.map.forEachFeatureAtPixel(
        evt.pixel,
        function (feature, layer) {
          return feature;
        }
      );
      if (feature && feature.getProperties().hasOwnProperty("id")) {
        let scName = feature.get("attributes").name;
        // Position the popup in the right world
        let worldWidth = getWidth(this.map.getView().getProjection().getExtent());
        let world = Math.floor((this.map.getCoordinateFromPixel(evt.pixel)[0] + worldWidth / 2) / worldWidth);

        this.showPopup.single = {
          name: scName,
          offset: {
            x: world * worldWidth
          }
        }
        this.updatePopups()
      } else {
        delete this.showPopup.single
        this.updatePopups()
      }
    });

    api.subscribe("tles")
      .then((dataBus) => {
        this.TLEsub = dataBus.subscribe((data: IDataTLEs) => {
          if (data) {
            this.TLES = data
          }
        })
      })

    this.updateNightLayer();
    this.intervals.push(setInterval(this.updateNightLayer, 600000))
    this.intervals.push(setInterval(this.updateLocations, 1000))
    this.updateSpacecraftLayers(this.spacecraftLayers, [])
  }

  destroyed() {
    this.intervals.forEach(i => {
      clearInterval(i)
    })
    if (this.TLEsub) {
      api.unsubscribe("tles")
        .then(() => {
        })
      this.TLEsub.unsubscribe()
    }
  }

  mapControlsZoomHandler(value: number) {
    let view = this.map.getView()
    view.setZoom((view.getZoom() || 1) + value)
  }
  mapControlsHomeHandler() {
    this.followSpacecraft = "";
    let view = this.map.getView();
    view.setZoom(this.ZOOM);
    view.setCenter(this.CENTER);
  }
  mapControlsFollowHandler() {
    if (this.config.followSpacecraft) {
      if (this.followSpacecraft === "") {
        this.followSpacecraft = this.config.followSpacecraft.name;
        this.updateFollowSpacecraft();
      } else {
        this.followSpacecraft = "";
      }
    }
  }
  mapControlsPopupHandler() {
    if (!this.showPopup.all) {
      this.showPopup.all = true
    } else {
      this.showPopup.all = false
    }
    this.updatePopups()
  }

  updateFollowSpacecraft() {
    if (this.followSpacecraft !== "") {
      const view = this.map.getView();
      const spacecraft = this.enabledSpacecraft.find(spacecraft => spacecraft.name === this.followSpacecraft)
      view.setCenter(spacecraft ? spacecraft.location : [0, 0]);
      view.setZoom(4);
    }
  }

  updateLocations() {
    this.spacecraftLayers = this.enabledSpacecraft.map(spacecraft => spacecraft.getLayer())

    this.updatePopups()
    this.updateFollowSpacecraft()
  }

  updatePopups() {
    if (this.showPopup.all) {
      this.popups = this.enabledSpacecraft
        .map((spacecraft) => {
          return spacecraft.getPopup(this.map.getTargetElement())
        })
    } else if (this.showPopup.single) {
      this.popups = this.enabledSpacecraft
        .filter((spacecraft) => spacecraft.name === this.showPopup.single?.name)
        .map((spacecraft) => spacecraft.getPopup(this.map.getTargetElement(), this.showPopup.single?.offset))
    } else {
      this.popups = []
    }
  }

  @Watch("popups")
  watchPopups(newValue: Array<Overlay>, oldValue: Array<Overlay>) {
    for (let overlay of Object.values(oldValue)) {
      this.map.removeOverlay(overlay);
    }
    for (let overlay of Object.values(newValue)) {
      this.map.addOverlay(overlay);
    }
    this.$forceUpdate();
  }

  @Watch("spacecraftLayers")
  updateSpacecraftLayers(newValue: Array<Layer>, oldValue: Array<Layer>) {
    for (let layer of oldValue) {
      this.map.removeLayer(layer);
    }
    for (let layer of newValue) {
      this.map.addLayer(layer);
    }
    this.$forceUpdate();
  }
}
